// Centralized program metadata used across nav, home cards and program pages.
export const PROGRAMS = [
    {
        slug: "school",
        path: "/programs/school",
        eyebrow: "Ages 8 — 18",
        title: "Elite Youth Preparation",
        tagline: "Build the engine. Sharpen the spring. Outwork the room.",
        image:
            "https://images.pexels.com/photos/13980451/pexels-photo-13980451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        summary:
            "Stage-correct athletic development for kids and teens. We coach speed, strength, elastic power and durability — the way it should be coached at every developmental window.",
    },
    {
        slug: "pro",
        path: "/programs/pro",
        eyebrow: "Ages 18 — 35",
        title: "Pursuing Pro",
        tagline: "Elite training for athletes chasing the next level.",
        image:
            "https://images.unsplash.com/photo-1755877956621-5fac2eae4ebb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxwcm8lMjBhdGhsZXRlJTIwc3ByaW50aW5nJTIwdHJhY2t8ZW58MHx8fHwxNzc3Mzk2NDUyfDA&ixlib=rb-4.1.0&q=85",
        summary:
            "Sport-specific performance programming for collegiate, semi-pro and pro hopefuls. Speed, power, recovery and combine prep — engineered, measured, repeated.",
    },
    {
        slug: "adult",
        path: "/programs/adult",
        eyebrow: "Adult Performance",
        title: "Adult Performance",
        tagline: "Look better. Feel better. Perform better — for the life you actually live.",
        image:
            "https://images.unsplash.com/photo-1709315957145-a4bad1feef28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwaW50ZW5zZSUyMGd5bSUyMHRyYWluaW5nJTIwZGFya3xlbnwwfHx8fDE3NzczOTY0NTJ8MA&ixlib=rb-4.1.0&q=85",
        summary:
            "For working professionals who still want to train like athletes. Programming built for long-term health, looking and feeling better, and performing in the life you actually live — career, family, weekend warriors, and the only body you'll ever have.",
    },
];

// Five athletic qualities every page references. Using simple icon strings so
// downstream components stay lean — they just import the matching lucide icon.
export const ATHLETIC_QUALITIES = [
    {
        key: "speed",
        label: "Speed",
        icon: "Zap",
        blurb: "Stride mechanics, contact times, max-velocity exposure — coached, not just trained.",
    },
    {
        key: "strength",
        label: "Strength",
        icon: "Dumbbell",
        blurb: "Force production from the ground up. Compensatory acceleration on every rep.",
    },
    {
        key: "elasticity",
        label: "Elasticity / Whip",
        icon: "Activity",
        blurb: "Stretch-shortening cycle. Tendons that snap. Reactive jumps and bounds.",
    },
    {
        key: "power",
        label: "Power",
        icon: "Flame",
        blurb: "Strength expressed at speed. Explosive force in the windows that matter.",
    },
    {
        key: "durability",
        label: "Durability",
        icon: "Shield",
        blurb: "Tissue tolerance, mobility, recovery rhythm — the season ends when the body says so.",
    },
];

// Three tiers under the Elite Youth Preparation umbrella.
// Each tier maps to a `/programs/school/<slug>` route and a dedicated detail page.
export const SCHOOL_TIERS = [
    {
        slug: "foundational",
        path: "/programs/school/foundational",
        ageRange: "8 — 10",
        title: "Foundational Athleticism",
        focus: "Movement Literacy & Coordination",
        eyebrow: "Stage 01 · The Foundation",
        headline:
            "Before strength comes coordination. Before power comes posture. We build the athlete from the ground up.",
        summary:
            "The 8 — 10 window is when the nervous system learns the language of athleticism. We teach kids how to sprint, jump, land, decelerate, change direction and absorb force — through play-led, coach-supervised athletic skill work. No max lifts. No grinding. Just clean, repeatable movement that makes every later stage possible.",
        // Five-pillar breakdown matching speed / strength / elasticity / power / durability
        qualities: [
            {
                key: "speed",
                label: "Speed",
                detail:
                    "Sprint posture, arm action, knee drive, and proper acceleration mechanics — taught at distances they can execute cleanly. Short sprints. Full recovery. Quality over quantity.",
            },
            {
                key: "strength",
                label: "Strength",
                detail:
                    "Bodyweight strength expressed through full ranges. Push-ups, squats, hangs, crawls, carries. The goal is owning every joint position, not adding load.",
            },
            {
                key: "elasticity",
                label: "Elasticity / Whip",
                detail:
                    "Skip variations, low-amplitude pogo hops, jump rope. Builds stiff ankles and reactive feet that pay off for the next decade.",
            },
            {
                key: "power",
                label: "Power",
                detail:
                    "Throwing patterns with med balls, broad jumps, standing long jumps. Submaximal but intentful — output without fatigue.",
            },
            {
                key: "durability",
                label: "Durability",
                detail:
                    "Mobility games, balance challenges, tumbling. Tissue gets exposed to a wide menu of shapes so growth spurts don't break them.",
            },
        ],
        bullets: [
            "Movement literacy: skip, sprint, hop, bound, throw, catch, decelerate, land",
            "Bodyweight strength held through full ranges of motion",
            "Reactive feet — short ground contacts on the floor and in shoes that allow it",
            "Coach-led, play-driven sessions that build attention span without crushing energy",
            "Coachability cues: posture, breath, focus before failure",
        ],
        sessionSnapshot: [
            { phase: "Warm-up", detail: "Locomotor circuit — skip, shuffle, crawl, animal patterns" },
            { phase: "Skill", detail: "Sprint or jump teach (one quality, 5–8 minutes of cueing)" },
            { phase: "Strength play", detail: "Bodyweight + med-ball circuit, low volume" },
            { phase: "Reactive", detail: "Pogo hops, skips, line-touches" },
            { phase: "Mobility", detail: "Movement game (tag, deceleration, balance)" },
        ],
    },
    {
        slug: "elite-youth",
        path: "/programs/school/elite-youth",
        ageRange: "11 — 14",
        title: "Elite Youth Performance",
        focus: "Capacity, Coordination & Force",
        eyebrow: "Stage 02 · The Build",
        headline:
            "The window where coordination explodes. We layer real strength on a foundation that already moves.",
        summary:
            "Between 11 and 14, the body is primed to absorb training. Bones are denser, the nervous system is hungry, and coordination scales fast when it's coached. We introduce structured strength work, top-end sprint mechanics, multi-directional agility, and teach the conjugate principle of training opposing qualities in the same week so nothing stagnates. Every session has a target, every block has a measurable outcome.",
        qualities: [
            {
                key: "speed",
                label: "Speed",
                detail:
                    "Acceleration mechanics under resistance and overspeed. Flying sprints to expose max velocity in tiny, recoverable doses. Posture under fatigue is non-negotiable.",
            },
            {
                key: "strength",
                label: "Strength",
                detail:
                    "Front squat, trap bar deadlift, push press, chin-up. Submaximal loads, perfect mechanics, intent on every rep — pushing the bar as fast as you can on every concentric.",
            },
            {
                key: "elasticity",
                label: "Elasticity / Whip",
                detail:
                    "Bound progressions, depth drops, ankle-stiffness work. We chase shorter ground contact times — the athletic difference that doesn't come from the weight room.",
            },
            {
                key: "power",
                label: "Power",
                detail:
                    "Trap-bar jumps, med-ball throws, jump-squat shaping. Strength expressed fast. Bar speed tracked, not just weight.",
            },
            {
                key: "durability",
                label: "Durability",
                detail:
                    "Posterior-chain bias, single-leg balance, hip and t-spine mobility, sleep + nutrition coaching. Growing kids break when nobody's watching the load.",
            },
        ],
        bullets: [
            "Acceleration mechanics — wall drills, sled marches, resisted starts",
            "Top-end speed exposure in short, fully-recovered doses",
            "Foundational barbell work coached perfectly: squat, deadlift, press, chin",
            "Multi-directional agility built off 1-cut, 2-cut decision drills",
            "Conjugate weekly template — speed day + strength day + reactive day",
            "Recovery & nutrition habit-stacks that travel with them to season",
        ],
        sessionSnapshot: [
            { phase: "Warm-up", detail: "Dynamic flow + activation circuit" },
            { phase: "Speed / Reactive", detail: "Sprint or jump quality (full recovery)" },
            { phase: "Strength A", detail: "Compound lift @ submaximal load, max bar speed" },
            { phase: "Strength B", detail: "Accessory chain — posterior, single-leg, anti-rotation" },
            { phase: "Conditioning", detail: "Sport-specific repeated effort, never grinding" },
        ],
    },
    {
        slug: "elite-peak",
        path: "/programs/school/elite-peak",
        ageRange: "14 — 18",
        title: "Elite Peak Performance",
        focus: "Sport-Specific Force & Velocity",
        eyebrow: "Stage 03 · The Edge",
        headline:
            "High school years are make-or-break. We coach the qualities that scouts actually measure.",
        summary:
            "By 14 the body can handle high-intent training. Now the question is: what does the next level look like, and what specific qualities will get this athlete there? We move past general preparation and into sport-specific peaking — alternating maximum-effort strength work, dynamic-effort speed-strength, and accommodating-resistance methods to develop force across the entire velocity curve. Every block has a tested outcome — sprint times, jump heights, lift PRs, broad jump, change-of-direction scores. We coach to the test.",
        qualities: [
            {
                key: "speed",
                label: "Speed",
                detail:
                    "Acceleration, max velocity and speed-endurance trained on separate days. Sprint volume kept low; quality kept high. Tempo work supports recovery without taxing the nervous system — the high-low approach.",
            },
            {
                key: "strength",
                label: "Strength",
                detail:
                    "Maximum-effort method 1–2x/week (squat, deadlift, bench variations rotated every 2–3 weeks). Compensatory acceleration on submaximal sets. Accommodating resistance with bands and chains where appropriate.",
            },
            {
                key: "elasticity",
                label: "Elasticity / Whip",
                detail:
                    "Depth jumps, altitude landings, hurdle hops, bounding progressions. Stiff ankles and reactive hips translate directly to faster sprints and more vertical at the rim.",
            },
            {
                key: "power",
                label: "Power",
                detail:
                    "Dynamic-effort method — speed bench, speed squat, speed pulls — to develop the rate-of-force-development that strength alone won't. Olympic lift derivatives where the athlete has earned the technique.",
            },
            {
                key: "durability",
                label: "Durability",
                detail:
                    "GPP circuits, sled drags, prowler pushes for restoration. Soft-tissue protocols. Sleep, nutrition and load-management dialed for in-season vs off-season blocks.",
            },
        ],
        bullets: [
            "Max-effort + dynamic-effort weekly split with rotated main lifts",
            "Top-end speed and acceleration trained as separate qualities",
            "Plyometric progression from extensive (low-intensity) to intensive (depth)",
            "Accommodating resistance where appropriate (bands, chains)",
            "Combine prep: 40, vertical, broad, pro-agility, 3-cone — coached, tested, repeated",
            "Restoration days that protect the nervous system through long seasons",
            "College and showcase prep — film, recruiting profile, position-specific battery",
        ],
        sessionSnapshot: [
            { phase: "Warm-up", detail: "Dynamic warm-up + CNS primer (jumps, throws)" },
            { phase: "Speed / Plyo", detail: "Acceleration OR max-velocity OR depth jumps (rotated)" },
            { phase: "Main lift", detail: "ME or DE main movement with rotated variation" },
            { phase: "Accessories", detail: "Posterior chain, single-leg, trunk anti-rotation" },
            { phase: "Conditioning", detail: "Position-specific repeated effort or restoration" },
        ],
    },
];
